import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import type DeviceDetectionService from 'madow-tech/services/device-detection';
import type { ColorPalette } from 'madow-tech/lib/canvas/shooting-stars';

// Import MarsCanvas directly - the actual canvas component
import MarsCanvas from 'madow-tech/components/mars-canvas';

interface LazyMarsCanvasSignature {
  Args: {
    colorPalette?: ColorPalette;
  };
  Element: HTMLDivElement;
}

/**
 * LazyMarsCanvas - Performance-optimized canvas wrapper
 *
 * On mobile devices, this component:
 * 1. Shows a CSS starfield placeholder immediately (fast FCP)
 * 2. Defers loading the actual canvas until browser is idle
 *
 * On desktop, loads the canvas immediately.
 *
 * This improves mobile Lighthouse scores by reducing initial JS execution
 * and allowing the page to become interactive faster.
 */
export default class LazyMarsCanvas extends Component<LazyMarsCanvasSignature> {
  @service declare deviceDetection: DeviceDetectionService;

  @tracked isCanvasReady = false;

  // Store the MarsCanvas component reference
  MarsCanvasComponent = MarsCanvas;

  constructor(owner: unknown, args: LazyMarsCanvasSignature['Args']) {
    super(owner, args);

    if (typeof window === 'undefined') {
      // SSR: don't load canvas
      return;
    }

    // Check if on mobile
    const isMobile = this.deviceDetection.isPhone;

    if (isMobile) {
      // Mobile: defer canvas loading until browser is idle
      this.deferCanvasLoad();
    } else {
      // Desktop: load immediately
      this.isCanvasReady = true;
    }

    registerDestructor(this, () => {
      // Cleanup if needed
    });
  }

  /**
   * Defer canvas loading using requestIdleCallback for mobile
   * Falls back to setTimeout for browsers without requestIdleCallback
   */
  deferCanvasLoad() {
    if ('requestIdleCallback' in window) {
      // Use requestIdleCallback with a timeout of 2 seconds
      // This ensures the canvas loads even if the browser stays busy
      (window as any).requestIdleCallback(
        () => {
          this.isCanvasReady = true;
        },
        { timeout: 2000 }
      );
    } else {
      // Fallback: load after 1 second
      setTimeout(() => {
        this.isCanvasReady = true;
      }, 1000);
    }
  }

  <template>
    <div class="lazy-mars-canvas-wrapper fixed inset-0 w-full -z-10" ...attributes>
      {{#if this.isCanvasReady}}
        {{! Render the actual canvas once ready }}
        <this.MarsCanvasComponent @colorPalette={{@colorPalette}} />
      {{else}}
        {{! CSS-only starfield placeholder - renders instantly, no JS needed }}
        {{! Styles are in app/styles/components/lazy-mars-canvas.css }}
        <div class="starfield-placeholder fixed inset-0 w-full h-[250vh] bg-black">
          {{! Static stars using CSS gradients - optimized for fast paint }}
          <div class="stars-layer-1"></div>
          <div class="stars-layer-2"></div>
          <div class="stars-layer-3"></div>
        </div>
      {{/if}}
    </div>
  </template>
}

